import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../../components/apiconfig/api-config';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import states from '../../../../components/common/states';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PurchaseBrokerForm = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    address: '',
    email_id: '',
    fax_no: '',
    website: '',
    currency: '',
    bank_name: '',
    account_no: '',
    swift_code: '',
    ifsc_code: '',
    ad_code: '',
    gst_no: '',
    aadhar_no: '',
    state: '',
    state_code: '',
  });
  const [originalFormData, setOriginalFormData] = useState({
    name: '',
    contact: '',
    address: '',
    email_id: '',
    fax_no: '',
    website: '',
    currency: '',
    bank_name: '',
    account_no: '',
    swift_code: '',
    ifsc_code: '',
    ad_code: '',
    gst_no: '',
    aadhar_no: '',
    state: '',
    state_code: '',
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  // const [message, setMessage] = useState(null); // No longer needed for errors, only for success if you want
  const navigate = useNavigate();

  // Fetch purchase broker data for editing
  useEffect(() => {
    if (isEditMode && id) {
      setInitialLoading(true);
      console.log('Fetching purchase broker with ID:', id);
      const fetchPurchaseBroker = async () => {
        try {
          const url = `${API_BASE_URL}/api/masters/purchase-broker-get/${id}`;
          console.log('Fetching from URL:', url);
          const res = await fetch(url, {
            credentials: 'include',
          });
          console.log('Response status:', res.status);
          if (!res.ok) {
            console.error('Response not ok:', res.status, res.statusText);
            throw new Error('Failed to fetch purchase broker');
          }
          const result = await res.json();
          console.log('Fetched purchase broker data:', result);
          if (result.data) {
            console.log('Setting form data:', result.data);
            const newFormData = {
              name: result.data.name ?? '',
              contact: result.data.contact ?? '',
              address: result.data.address ?? '',
              email_id: result.data.email_id ?? '',
              fax_no: result.data.fax_no ?? '',
              website: result.data.website ?? '',
              currency: result.data.currency ?? '',
              bank_name: result.data.bank_name ?? '',
              account_no: result.data.account_no ?? '',
              swift_code: result.data.swift_code ?? '',
              ifsc_code: result.data.ifsc_code ?? '',
              ad_code: result.data.ad_code ?? '',
              gst_no: result.data.gst_no ?? '',
              aadhar_no: result.data.aadhar_no ?? '',
              state: result.data.state ?? '',
              state_code: result.data.state_code ?? ''
            };
            console.log('Setting new form data:', newFormData);
            setFormData(newFormData);
            setOriginalFormData(newFormData); // Store original values for read-only check
          } else {
            toast.error('Purchase Broker not found');
            navigate('/purchase-broker');
          }
        } catch (error) {
          console.error('Error fetching purchase broker:', error);
          toast.error('Failed to load purchase broker data');
          navigate('/purchase-broker');
        } finally {
          setInitialLoading(false);
        }
      };
      fetchPurchaseBroker();
    }
  }, [id, isEditMode, navigate]);

  // Debug: Monitor formData changes
  useEffect(() => {
    console.log('FormData changed:', formData);
  }, [formData]);

  const FIELD_LIMITS = {
    name: 100,
    contact: 50,
    address: 500,
    email_id: 100,
    fax_no: 30,
    website: 100,
    currency: 10,
    bank_name: 100,
    account_no: 50,
    swift_code: 50,
    ifsc_code: 20,
    ad_code: 50,
    gst_no: 45,
    aadhar_no: 50,
    state: 45,
    state_code: 45,
  };

  const validateField = (name, value) => {
    let error = '';
    const limit = FIELD_LIMITS[name];
    if (limit && value && value.length > limit) {
      error = `Maximum ${limit} characters allowed for ${name.replace('_', ' ')}`;
    }
    // Required fields
    const requiredFields = ['name', 'contact', 'address', 'email_id', 'currency', 'bank_name', 'account_no', 'state'];
    if (requiredFields.includes(name) && (!value || value.trim() === '')) {
      error = `${name.replace('_', ' ')} is required`;
    }
    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Check if field is read-only (has existing value from database)
    const isReadOnly = (fieldName) => {
      // Only check if this is edit mode and the field had an original value
      if (!isEditMode) return false;
      
      // Get the original value from the database (stored separately)
      const originalValue = originalFormData[fieldName];
      return originalValue && originalValue.toString().trim() !== '';
    };
    
    // Don't allow changes to read-only fields
    if (isReadOnly(name)) {
      return;
    }
    
    const limit = FIELD_LIMITS[name];
    if (limit && value.length > limit) {
      toast.error(`Maximum ${limit} characters allowed for ${name.replace('_', ' ')}`);
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleStateChange = (e) => {
    const selectedState = e.target.value;
    const stateObj = states.find(s => s.name === selectedState);
    setFormData(prev => ({
      ...prev,
      state: selectedState,
      state_code: stateObj ? stateObj.code : ''
    }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      toast.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // setMessage(null);
    // Validation: check all fields for max length
    for (const key in formData) {
      if (FIELD_LIMITS[key] && formData[key] && formData[key].length > FIELD_LIMITS[key]) {
        toast.error(`Maximum ${FIELD_LIMITS[key]} characters allowed for ${key.replace('_', ' ')}`);
        setLoading(false);
        return;
      }
    }
    // Validation: check required fields
    const requiredFields = ['name', 'contact', 'address', 'email_id', 'currency', 'bank_name', 'account_no', 'state'];
    for (const field of requiredFields) {
      if (!formData[field] || formData[field].trim() === '') {
        toast.error(`${field.replace('_', ' ')} is required`);
        setLoading(false);
        return;
      }
    }
    try {
      const url = isEditMode 
        ? `${API_BASE_URL}/api/masters/purchase-broker-put/${id}`
        : `${API_BASE_URL}/api/masters/purchase-broker-post`;
      
      console.log('=== SUBMITTING PURCHASE BROKER DATA ===');
      console.log('URL:', url);
      console.log('Method:', isEditMode ? 'PUT' : 'POST');
      console.log('Form data being sent:', formData);
      
      const res = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(isEditMode ? 'Purchase Broker Updated Successfully!' : 'Purchase Broker Registered Successfully!');
        if (!isEditMode) {
          setFormData({
            name: '',
            contact: '',
            address: '',
            email_id: '',
            fax_no: '',
            website: '',
            currency: '',
            bank_name: '',
            account_no: '',
            swift_code: '',
            ifsc_code: '',
            ad_code: '',
            gst_no: '',
            aadhar_no: '',
            state: '',
            state_code: '',
          });
        } else {
          navigate('/purchase-broker');
        }
      } else {
        toast.error(data.error || 'Something went wrong');
      }
    } catch (err) {
      toast.error('Server Error');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <ToastContainer />
      {initialLoading ? (
        <div className="text-center py-8 text-blue-600">Loading...</div>
      ) : (
        <>
          <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-4 py-1.5">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-bold mb-0 text-left">{isEditMode ? 'Edit Purchase Broker' : 'Purchase Broker Entry'}</h2>
              {isEditMode && (
                <div className="text-xs text-gray-600">
                  <span className="inline-block w-3 h-3 bg-gray-100 border border-gray-300 mr-1"></span>
                  Read-only fields
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 text-[#10a5e9] border border-[#10a5e9] px-2 py-0.5 rounded text-xs font-medium hover:bg-[#eaf6fd]"
            >
              <FaArrowLeft />
              Back
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-0">
        <div className="px-4 pt-4 pb-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Name</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.name} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" required />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Contact</label>
              <input type="text" name="contact" value={formData.contact} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.contact} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" required />
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-medium mb-1">Address</label>
              <textarea name="address" value={formData.address} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.address} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none resize-none" rows={2}></textarea>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Email ID</label>
              <input type="email" name="email_id" value={formData.email_id} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.email_id} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Fax No</label>
              <input type="text" name="fax_no" value={formData.fax_no} onChange={handleChange} maxLength={FIELD_LIMITS.fax_no} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Website</label>
              <input type="text" name="website" value={formData.website} onChange={handleChange} maxLength={FIELD_LIMITS.website} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Currency</label>
              <input type="text" name="currency" value={formData.currency} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.currency} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Bank Name</label>
              <input type="text" name="bank_name" value={formData.bank_name} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.bank_name} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Account No</label>
              <input type="text" name="account_no" value={formData.account_no} onChange={handleChange} onBlur={handleBlur} maxLength={FIELD_LIMITS.account_no} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">SWIFT Code</label>
              <input type="text" name="swift_code" value={formData.swift_code} onChange={handleChange} maxLength={FIELD_LIMITS.swift_code} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">IFSC Code</label>
              <input type="text" name="ifsc_code" value={formData.ifsc_code} onChange={handleChange} maxLength={FIELD_LIMITS.ifsc_code} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">AD Code</label>
              <input type="text" name="ad_code" value={formData.ad_code} onChange={handleChange} maxLength={FIELD_LIMITS.ad_code} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">GST No</label>
              <input type="text" name="gst_no" value={formData.gst_no} onChange={handleChange} maxLength={FIELD_LIMITS.gst_no} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">State</label>
              <select 
                name="state" 
                value={formData.state} 
                onChange={handleStateChange}
                onBlur={handleBlur}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none"
              >
                <option value="">Select State</option>
                {states.map((state, index) => (
                  <option key={index} value={state.name}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">State Code</label>
              <input 
                type="text" 
                name="state_code" 
                value={formData.state_code} 
                readOnly
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-gray-100 focus:outline-none rounded-none" 
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Aadhar No</label>
              <input type="text" name="aadhar_no" value={formData.aadhar_no} onChange={handleChange} maxLength={FIELD_LIMITS.aadhar_no} className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" />
            </div>
          </div>
        </div>

        <div className="px-4 pt-2 pb-4">
          <button
            type="submit"
            className="w-auto px-4 py-1.5 text-sm bg-[#10a5e9] text-white font-semibold tracking-wide rounded border border-[#10a5e9] hover:bg-[#0e8ac2] transition disabled:opacity-60"
            disabled={loading}
          >
            {loading ? 'Saving...' : (isEditMode ? 'Update Purchase Broker' : 'Save Purchase Broker')}
          </button>
        </div>
      </form>
        </>
      )}
    </div>
  );
};

export default PurchaseBrokerForm;